/**
 * Local exercise catalog — used as fallback when DB exercises table
 * is unavailable or empty.
 *
 * The 5 baseline lifts are marked is_big_three: true (historical name;
 * covers all 5 core lifts per the baseline invariant).
 *
 * Categories map to BODY_PARTS in the record screen.
 * Synced with migration 20260303000000_exercise_catalog_expansion.sql
 */

import type { Exercise } from '@/types/database';

const T = '1970-01-01T00:00:00.000Z';
const ex = (id: string, name: string, category: string, body_part: string, baseline = false): Exercise => ({
  id: `local-${id}`,
  name,
  category,
  body_part,
  is_big_three: baseline,
  is_custom: false,
  created_at: T,
});

// ── 5 Baseline Lifts (always prominent) ──
const BASELINE: Exercise[] = [
  ex('back-squat', 'Back Squat', 'legs', 'legs', true),
  ex('deadlift', 'Deadlift', 'back', 'back', true),
  ex('bench-press', 'Bench Press', 'chest', 'chest', true),
  ex('weighted-pull-up', 'Weighted Pull-Up', 'back', 'back', true),
  ex('dead-hang', 'Dead Hang', 'back', 'back', true),
];

// ── Barbell — Compounds ──
const BARBELL: Exercise[] = [
  ex('overhead-press', 'Overhead Press', 'shoulders', 'shoulders'),
  ex('barbell-row', 'Barbell Row', 'back', 'back'),
  ex('front-squat', 'Front Squat', 'legs', 'legs'),
  ex('sumo-deadlift', 'Sumo Deadlift', 'back', 'back'),
  ex('romanian-deadlift', 'Romanian Deadlift', 'legs', 'legs'),
  ex('hip-thrust', 'Hip Thrust', 'legs', 'legs'),
  ex('close-grip-bench', 'Close-Grip Bench Press', 'chest', 'chest'),
  ex('incline-bench-press', 'Incline Bench Press', 'chest', 'chest'),
  ex('barbell-lunge', 'Barbell Lunge', 'legs', 'legs'),
  ex('pendlay-row', 'Pendlay Row', 'back', 'back'),
  ex('push-press', 'Push Press', 'shoulders', 'shoulders'),
  ex('rack-pull', 'Rack Pull', 'back', 'back'),
  ex('barbell-good-morning', 'Barbell Good Morning', 'back', 'back'),
  ex('deficit-deadlift', 'Deficit Deadlift', 'back', 'back'),
  ex('pause-squat', 'Pause Squat', 'legs', 'legs'),
  ex('pin-squat', 'Pin Squat', 'legs', 'legs'),
  ex('zercher-squat', 'Zercher Squat', 'legs', 'legs'),
  ex('floor-press', 'Floor Press', 'chest', 'chest'),
  ex('decline-bench-press', 'Decline Bench Press', 'chest', 'chest'),
  ex('t-bar-row', 'T-Bar Row', 'back', 'back'),
  ex('landmine-press', 'Landmine Press', 'shoulders', 'shoulders'),
  ex('landmine-row', 'Landmine Row', 'back', 'back'),
  ex('barbell-step-up', 'Barbell Step-Up', 'legs', 'legs'),
  ex('barbell-bulgarian-split-squat', 'Barbell Bulgarian Split Squat', 'legs', 'legs'),
  ex('behind-the-neck-press', 'Behind-the-Neck Press', 'shoulders', 'shoulders'),
  ex('snatch-grip-deadlift', 'Snatch-Grip Deadlift', 'back', 'back'),
  ex('thruster', 'Thruster', 'full_body', 'full_body'),
  ex('barbell-hack-squat', 'Barbell Hack Squat', 'legs', 'legs'),
  ex('anderson-squat', 'Anderson Squat', 'legs', 'legs'),
  ex('safety-bar-squat', 'Safety Bar Squat', 'legs', 'legs'),
  ex('barbell-walking-lunge', 'Barbell Walking Lunge', 'legs', 'legs'),
  ex('barbell-reverse-lunge', 'Barbell Reverse Lunge', 'legs', 'legs'),
  ex('barbell-lateral-lunge', 'Barbell Lateral Lunge', 'legs', 'legs'),
  ex('wide-grip-bench', 'Wide-Grip Bench Press', 'chest', 'chest'),
  ex('spoto-press', 'Spoto Press', 'chest', 'chest'),
  ex('board-press', 'Board Press', 'chest', 'chest'),
  ex('larsen-press', 'Larsen Press', 'chest', 'chest'),
  ex('meadows-row', 'Meadows Row', 'back', 'back'),
  ex('seal-row', 'Seal Row', 'back', 'back'),
  ex('yates-row', 'Yates Row', 'back', 'back'),
  ex('underhand-row', 'Bent-Over Row (Underhand)', 'back', 'back'),
  ex('barbell-upright-row', 'Barbell Upright Row', 'shoulders', 'shoulders'),
  ex('bradford-press', 'Bradford Press', 'shoulders', 'shoulders'),
  ex('z-press', 'Z-Press', 'shoulders', 'shoulders'),
  ex('viking-press', 'Viking Press', 'shoulders', 'shoulders'),
  ex('barbell-hip-abduction', 'Barbell Hip Abduction', 'legs', 'legs'),
  ex('stiff-leg-deadlift', 'Stiff-Leg Deadlift', 'legs', 'legs'),
  ex('block-pull', 'Block Pull', 'back', 'back'),
  ex('paused-deadlift', 'Paused Deadlift', 'back', 'back'),
  ex('barbell-glute-bridge', 'Barbell Glute Bridge', 'legs', 'legs'),
  // Barbell Isolations
  ex('barbell-curl', 'Barbell Curl', 'arms', 'arms'),
  ex('barbell-skull-crusher', 'Barbell Skull Crusher', 'arms', 'arms'),
  ex('barbell-reverse-curl', 'Barbell Reverse Curl', 'arms', 'arms'),
  ex('barbell-wrist-curl', 'Barbell Wrist Curl', 'arms', 'arms'),
  ex('barbell-reverse-wrist-curl', 'Barbell Reverse Wrist Curl', 'arms', 'arms'),
  ex('barbell-shrug', 'Barbell Shrug', 'back', 'back'),
  ex('barbell-calf-raise', 'Barbell Calf Raise', 'legs', 'legs'),
  ex('barbell-preacher-curl', 'Barbell Preacher Curl', 'arms', 'arms'),
  ex('barbell-drag-curl', 'Barbell Drag Curl', 'arms', 'arms'),
  ex('jm-press', 'JM Press', 'arms', 'arms'),
  ex('barbell-pullover', 'Barbell Pullover', 'chest', 'chest'),
];

// ── Olympic Lifts ──
const OLYMPIC: Exercise[] = [
  ex('power-clean', 'Power Clean', 'full_body', 'full_body'),
  ex('clean-and-jerk', 'Clean and Jerk', 'full_body', 'full_body'),
  ex('snatch', 'Snatch', 'full_body', 'full_body'),
  ex('power-snatch', 'Power Snatch', 'full_body', 'full_body'),
  ex('hang-clean', 'Hang Clean', 'full_body', 'full_body'),
  ex('hang-snatch', 'Hang Snatch', 'full_body', 'full_body'),
  ex('clean-pull', 'Clean Pull', 'full_body', 'full_body'),
  ex('snatch-pull', 'Snatch Pull', 'full_body', 'full_body'),
  ex('clean-high-pull', 'Clean High Pull', 'full_body', 'full_body'),
  ex('muscle-clean', 'Muscle Clean', 'full_body', 'full_body'),
  ex('muscle-snatch', 'Muscle Snatch', 'full_body', 'full_body'),
  ex('hang-power-clean', 'Hang Power Clean', 'full_body', 'full_body'),
  ex('hang-power-snatch', 'Hang Power Snatch', 'full_body', 'full_body'),
  ex('split-jerk', 'Split Jerk', 'full_body', 'full_body'),
  ex('push-jerk', 'Push Jerk', 'full_body', 'full_body'),
  ex('squat-clean', 'Squat Clean', 'full_body', 'full_body'),
  ex('squat-snatch', 'Squat Snatch', 'full_body', 'full_body'),
  ex('overhead-squat', 'Overhead Squat', 'full_body', 'full_body'),
  ex('snatch-balance', 'Snatch Balance', 'full_body', 'full_body'),
  ex('clean-deadlift', 'Clean Deadlift', 'full_body', 'full_body'),
  ex('snatch-deadlift', 'Snatch Deadlift', 'full_body', 'full_body'),
];

// ── EZ Curl Bar ──
const EZ_CURL: Exercise[] = [
  ex('ez-bar-curl', 'EZ Bar Curl', 'arms', 'arms'),
  ex('ez-bar-preacher-curl', 'EZ Bar Preacher Curl', 'arms', 'arms'),
  ex('ez-bar-skull-crusher', 'EZ Bar Skull Crusher', 'arms', 'arms'),
  ex('ez-bar-reverse-curl', 'EZ Bar Reverse Curl', 'arms', 'arms'),
  ex('ez-bar-spider-curl', 'EZ Bar Spider Curl', 'arms', 'arms'),
  ex('ez-bar-upright-row', 'EZ Bar Upright Row', 'shoulders', 'shoulders'),
  ex('ez-bar-close-grip-press', 'EZ Bar Close-Grip Press', 'chest', 'chest'),
  ex('ez-bar-overhead-ext', 'EZ Bar Overhead Extension', 'arms', 'arms'),
];

// ── Trap Bar ──
const TRAP_BAR: Exercise[] = [
  ex('trap-bar-deadlift', 'Trap Bar Deadlift', 'back', 'back'),
  ex('trap-bar-shrug', 'Trap Bar Shrug', 'back', 'back'),
  ex('trap-bar-row', 'Trap Bar Row', 'back', 'back'),
  ex('trap-bar-carry', 'Trap Bar Carry', 'full_body', 'full_body'),
  ex('trap-bar-jump', 'Trap Bar Jump', 'legs', 'legs'),
  ex('trap-bar-rdl', 'Trap Bar Romanian Deadlift', 'legs', 'legs'),
  ex('trap-bar-floor-press', 'Trap Bar Floor Press', 'chest', 'chest'),
];

// ── Smith Machine ──
const SMITH: Exercise[] = [
  ex('smith-squat', 'Smith Machine Squat', 'legs', 'legs'),
  ex('smith-bench-press', 'Smith Machine Bench Press', 'chest', 'chest'),
  ex('smith-incline-press', 'Smith Machine Incline Press', 'chest', 'chest'),
  ex('smith-shoulder-press', 'Smith Machine Shoulder Press', 'shoulders', 'shoulders'),
  ex('smith-lunge', 'Smith Machine Lunge', 'legs', 'legs'),
  ex('smith-calf-raise', 'Smith Machine Calf Raise', 'legs', 'legs'),
  ex('smith-row', 'Smith Machine Row', 'back', 'back'),
  ex('smith-hip-thrust', 'Smith Machine Hip Thrust', 'legs', 'legs'),
  ex('smith-rdl', 'Smith Machine Romanian Deadlift', 'legs', 'legs'),
  ex('smith-decline-press', 'Smith Machine Decline Press', 'chest', 'chest'),
  ex('smith-close-grip-bench', 'Smith Machine Close-Grip Bench', 'chest', 'chest'),
  ex('smith-split-squat', 'Smith Machine Split Squat', 'legs', 'legs'),
  ex('smith-reverse-lunge', 'Smith Machine Reverse Lunge', 'legs', 'legs'),
  ex('smith-upright-row', 'Smith Machine Upright Row', 'shoulders', 'shoulders'),
  ex('smith-good-morning', 'Smith Machine Good Morning', 'back', 'back'),
  ex('smith-drag-curl', 'Smith Machine Drag Curl', 'arms', 'arms'),
  ex('smith-btb-shrug', 'Smith Machine Behind-the-Back Shrug', 'back', 'back'),
];

// ── Dumbbell ──
const DUMBBELL: Exercise[] = [
  ex('dumbbell-bench-press', 'Dumbbell Bench Press', 'chest', 'chest'),
  ex('dumbbell-shoulder-press', 'Dumbbell Shoulder Press', 'shoulders', 'shoulders'),
  ex('dumbbell-row', 'Dumbbell Row', 'back', 'back'),
  ex('dumbbell-curl', 'Dumbbell Curl', 'arms', 'arms'),
  ex('dumbbell-lateral-raise', 'Dumbbell Lateral Raise', 'shoulders', 'shoulders'),
  ex('dumbbell-fly', 'Dumbbell Fly', 'chest', 'chest'),
  ex('dumbbell-lunge', 'Dumbbell Lunge', 'legs', 'legs'),
  ex('goblet-squat', 'Goblet Squat', 'legs', 'legs'),
  ex('dumbbell-tricep-extension', 'Dumbbell Tricep Extension', 'arms', 'arms'),
  ex('hammer-curl', 'Hammer Curl', 'arms', 'arms'),
  ex('dumbbell-shrug', 'Dumbbell Shrug', 'back', 'back'),
  ex('dumbbell-pullover', 'Dumbbell Pullover', 'chest', 'chest'),
  ex('dumbbell-reverse-fly', 'Dumbbell Reverse Fly', 'shoulders', 'shoulders'),
  ex('concentration-curl', 'Concentration Curl', 'arms', 'arms'),
  ex('dumbbell-incline-press', 'Dumbbell Incline Press', 'chest', 'chest'),
  ex('dumbbell-incline-curl', 'Dumbbell Incline Curl', 'arms', 'arms'),
  ex('dumbbell-decline-press', 'Dumbbell Decline Press', 'chest', 'chest'),
  ex('dumbbell-arnold-press', 'Dumbbell Arnold Press', 'shoulders', 'shoulders'),
  ex('dumbbell-front-raise', 'Dumbbell Front Raise', 'shoulders', 'shoulders'),
  ex('dumbbell-kickback', 'Dumbbell Kickback', 'arms', 'arms'),
  ex('dumbbell-bulgarian-split-squat', 'Dumbbell Bulgarian Split Squat', 'legs', 'legs'),
  ex('dumbbell-step-up', 'Dumbbell Step-Up', 'legs', 'legs'),
  ex('dumbbell-rdl', 'Dumbbell Romanian Deadlift', 'legs', 'legs'),
  ex('dumbbell-calf-raise', 'Dumbbell Calf Raise', 'legs', 'legs'),
  ex('dumbbell-floor-press', 'Dumbbell Floor Press', 'chest', 'chest'),
  ex('single-arm-dumbbell-row', 'Single-Arm Dumbbell Row', 'back', 'back'),
  ex('single-arm-dumbbell-press', 'Single-Arm Dumbbell Press', 'shoulders', 'shoulders'),
  ex('single-arm-dumbbell-curl', 'Single-Arm Dumbbell Curl', 'arms', 'arms'),
  ex('dumbbell-sumo-squat', 'Dumbbell Sumo Squat', 'legs', 'legs'),
  ex('dumbbell-neutral-grip-press', 'Dumbbell Chest Press (Neutral Grip)', 'chest', 'chest'),
  ex('dumbbell-zottman-curl', 'Dumbbell Zottman Curl', 'arms', 'arms'),
  ex('dumbbell-upright-row', 'Dumbbell Upright Row', 'shoulders', 'shoulders'),
  ex('renegade-row', 'Renegade Row', 'back', 'back'),
  ex('dumbbell-thruster', 'Dumbbell Thruster', 'full_body', 'full_body'),
  ex('dumbbell-snatch', 'Dumbbell Snatch', 'full_body', 'full_body'),
  ex('dumbbell-walking-lunge', 'Dumbbell Walking Lunge', 'legs', 'legs'),
  ex('dumbbell-reverse-lunge', 'Dumbbell Reverse Lunge', 'legs', 'legs'),
  ex('dumbbell-squeeze-press', 'Dumbbell Squeeze Press', 'chest', 'chest'),
  ex('dumbbell-incline-fly', 'Dumbbell Incline Fly', 'chest', 'chest'),
  ex('dumbbell-decline-fly', 'Dumbbell Decline Fly', 'chest', 'chest'),
  ex('dumbbell-lateral-lunge', 'Dumbbell Lateral Lunge', 'legs', 'legs'),
  ex('dumbbell-clean', 'Dumbbell Clean', 'full_body', 'full_body'),
  ex('dumbbell-clean-and-press', 'Dumbbell Clean and Press', 'full_body', 'full_body'),
  ex('dumbbell-swing', 'Dumbbell Swing', 'full_body', 'full_body'),
  ex('dumbbell-wrist-curl', 'Dumbbell Wrist Curl', 'arms', 'arms'),
  ex('dumbbell-reverse-wrist-curl', 'Dumbbell Reverse Wrist Curl', 'arms', 'arms'),
  ex('dumbbell-skull-crusher', 'Dumbbell Skull Crusher', 'arms', 'arms'),
  ex('dumbbell-preacher-curl', 'Dumbbell Preacher Curl', 'arms', 'arms'),
  ex('dumbbell-spider-curl', 'Dumbbell Spider Curl', 'arms', 'arms'),
  ex('dumbbell-overhead-tricep-ext', 'Dumbbell Overhead Tricep Extension', 'arms', 'arms'),
  ex('single-arm-db-ohp', 'Single-Arm Dumbbell Overhead Press', 'shoulders', 'shoulders'),
  ex('dumbbell-cuban-press', 'Dumbbell Cuban Press', 'shoulders', 'shoulders'),
  ex('dumbbell-y-raise', 'Dumbbell Y-Raise', 'shoulders', 'shoulders'),
  ex('dumbbell-lu-raise', 'Dumbbell Lu Raise', 'shoulders', 'shoulders'),
  ex('dumbbell-deadlift', 'Dumbbell Deadlift', 'legs', 'legs'),
  ex('single-leg-db-deadlift', 'Single-Leg Dumbbell Deadlift', 'legs', 'legs'),
  ex('dumbbell-curtsy-lunge', 'Dumbbell Curtsy Lunge', 'legs', 'legs'),
  ex('dumbbell-goblet-reverse-lunge', 'Dumbbell Goblet Reverse Lunge', 'legs', 'legs'),
  ex('dumbbell-single-leg-calf-raise', 'Dumbbell Single-Leg Calf Raise', 'legs', 'legs'),
];

// ── Cable / Machine ──
const CABLE: Exercise[] = [
  ex('lat-pulldown', 'Lat Pulldown', 'back', 'back'),
  ex('cable-row', 'Cable Row', 'back', 'back'),
  ex('leg-press', 'Leg Press', 'legs', 'legs'),
  ex('cable-fly', 'Cable Fly', 'chest', 'chest'),
  ex('tricep-pushdown', 'Tricep Pushdown', 'arms', 'arms'),
  ex('cable-curl', 'Cable Curl', 'arms', 'arms'),
  ex('face-pull', 'Face Pull', 'shoulders', 'shoulders'),
  ex('cable-lateral-raise', 'Cable Lateral Raise', 'shoulders', 'shoulders'),
  ex('leg-curl', 'Leg Curl', 'legs', 'legs'),
  ex('leg-extension', 'Leg Extension', 'legs', 'legs'),
  ex('calf-raise-machine', 'Calf Raise (Machine)', 'legs', 'legs'),
  ex('seated-row', 'Seated Row', 'back', 'back'),
  ex('chest-press-machine', 'Chest Press (Machine)', 'chest', 'chest'),
  ex('hack-squat', 'Hack Squat', 'legs', 'legs'),
  ex('pec-deck', 'Pec Deck', 'chest', 'chest'),
  ex('reverse-pec-deck', 'Reverse Pec Deck', 'shoulders', 'shoulders'),
  ex('shoulder-press-machine', 'Shoulder Press (Machine)', 'shoulders', 'shoulders'),
  ex('assisted-pull-up', 'Assisted Pull-Up (Machine)', 'back', 'back'),
  ex('assisted-dip', 'Assisted Dip (Machine)', 'chest', 'chest'),
  ex('cable-crossover', 'Cable Crossover', 'chest', 'chest'),
  ex('cable-overhead-tricep-ext', 'Cable Overhead Tricep Extension', 'arms', 'arms'),
  ex('cable-kickback', 'Cable Kickback', 'arms', 'arms'),
  ex('cable-woodchop', 'Cable Woodchop', 'core', 'core'),
  ex('single-arm-cable-row', 'Single-Arm Cable Row', 'back', 'back'),
  ex('single-arm-lat-pulldown', 'Single-Arm Lat Pulldown', 'back', 'back'),
  ex('cable-pull-through', 'Cable Pull-Through', 'legs', 'legs'),
  ex('hip-abduction-machine', 'Hip Abduction (Machine)', 'legs', 'legs'),
  ex('hip-adduction-machine', 'Hip Adduction (Machine)', 'legs', 'legs'),
  ex('seated-calf-raise-machine', 'Seated Calf Raise (Machine)', 'legs', 'legs'),
  ex('standing-calf-raise-machine', 'Standing Calf Raise (Machine)', 'legs', 'legs'),
  ex('v-squat-machine', 'V-Squat (Machine)', 'legs', 'legs'),
  ex('pendulum-squat', 'Pendulum Squat', 'legs', 'legs'),
  ex('belt-squat-machine', 'Belt Squat (Machine)', 'legs', 'legs'),
  ex('cable-reverse-curl', 'Cable Reverse Curl', 'arms', 'arms'),
  ex('cable-hammer-curl', 'Cable Hammer Curl', 'arms', 'arms'),
  ex('close-grip-lat-pulldown', 'Close-Grip Lat Pulldown', 'back', 'back'),
  ex('straight-arm-pulldown', 'Straight-Arm Pulldown', 'back', 'back'),
  ex('single-leg-leg-press', 'Single-Leg Leg Press', 'legs', 'legs'),
  ex('single-leg-leg-curl', 'Single-Leg Leg Curl', 'legs', 'legs'),
  ex('single-leg-leg-extension', 'Single-Leg Leg Extension', 'legs', 'legs'),
  ex('cable-upright-row', 'Cable Upright Row', 'shoulders', 'shoulders'),
  ex('cable-front-raise', 'Cable Front Raise', 'shoulders', 'shoulders'),
  ex('cable-rear-delt-fly', 'Cable Rear Delt Fly', 'shoulders', 'shoulders'),
  ex('cable-bayesian-curl', 'Cable Bayesian Curl', 'arms', 'arms'),
  ex('cable-concentration-curl', 'Cable Concentration Curl', 'arms', 'arms'),
  ex('rope-tricep-pushdown', 'Rope Tricep Pushdown', 'arms', 'arms'),
  ex('v-bar-tricep-pushdown', 'V-Bar Tricep Pushdown', 'arms', 'arms'),
  ex('cable-wrist-curl', 'Cable Wrist Curl', 'arms', 'arms'),
  ex('wide-grip-lat-pulldown', 'Wide-Grip Lat Pulldown', 'back', 'back'),
  ex('reverse-grip-lat-pulldown', 'Reverse-Grip Lat Pulldown', 'back', 'back'),
  ex('cable-shrug', 'Cable Shrug', 'back', 'back'),
  ex('seated-leg-curl', 'Seated Leg Curl', 'legs', 'legs'),
  ex('lying-leg-curl', 'Lying Leg Curl', 'legs', 'legs'),
  ex('glute-kickback-machine', 'Glute Kickback (Machine)', 'legs', 'legs'),
  ex('incline-chest-press-machine', 'Incline Chest Press (Machine)', 'chest', 'chest'),
  ex('decline-chest-press-machine', 'Decline Chest Press (Machine)', 'chest', 'chest'),
  ex('lat-pulldown-machine', 'Lat Pulldown (Machine)', 'back', 'back'),
  ex('low-row-machine', 'Low Row (Machine)', 'back', 'back'),
  ex('high-row-machine', 'High Row (Machine)', 'back', 'back'),
  ex('rear-delt-machine', 'Rear Delt Machine', 'shoulders', 'shoulders'),
  ex('lateral-raise-machine', 'Lateral Raise (Machine)', 'shoulders', 'shoulders'),
  ex('preacher-curl-machine', 'Preacher Curl (Machine)', 'arms', 'arms'),
  ex('bicep-curl-machine', 'Bicep Curl (Machine)', 'arms', 'arms'),
  ex('tricep-extension-machine', 'Tricep Extension (Machine)', 'arms', 'arms'),
  ex('tricep-dip-machine', 'Tricep Dip (Machine)', 'arms', 'arms'),
  ex('cable-ab-crunch', 'Cable Ab Crunch (Kneeling)', 'core', 'core'),
  ex('cable-reverse-woodchop', 'Cable Reverse Woodchop', 'core', 'core'),
  ex('rotary-torso-machine', 'Rotary Torso (Machine)', 'core', 'core'),
  ex('ab-crunch-machine', 'Ab Crunch (Machine)', 'core', 'core'),
  ex('back-extension-machine', 'Back Extension (Machine)', 'back', 'back'),
];

// ── Kettlebell ──
const KETTLEBELL: Exercise[] = [
  ex('kettlebell-swing', 'Kettlebell Swing', 'full_body', 'full_body'),
  ex('kettlebell-goblet-squat', 'Kettlebell Goblet Squat', 'legs', 'legs'),
  ex('kettlebell-turkish-getup', 'Kettlebell Turkish Get-Up', 'full_body', 'full_body'),
  ex('kettlebell-clean', 'Kettlebell Clean', 'full_body', 'full_body'),
  ex('kettlebell-press', 'Kettlebell Press', 'shoulders', 'shoulders'),
  ex('kettlebell-snatch', 'Kettlebell Snatch', 'full_body', 'full_body'),
  ex('kettlebell-row', 'Kettlebell Row', 'back', 'back'),
  ex('kettlebell-windmill', 'Kettlebell Windmill', 'core', 'core'),
  ex('kettlebell-halo', 'Kettlebell Halo', 'shoulders', 'shoulders'),
  ex('kettlebell-deadlift', 'Kettlebell Deadlift', 'legs', 'legs'),
  ex('kettlebell-bottoms-up-press', 'Kettlebell Bottoms-Up Press', 'shoulders', 'shoulders'),
  ex('kettlebell-thruster', 'Kettlebell Thruster', 'full_body', 'full_body'),
  ex('single-arm-kb-swing', 'Single-Arm Kettlebell Swing', 'full_body', 'full_body'),
  ex('kettlebell-clean-and-press', 'Kettlebell Clean and Press', 'full_body', 'full_body'),
  ex('kettlebell-sumo-deadlift', 'Kettlebell Sumo Deadlift', 'legs', 'legs'),
  ex('kettlebell-lunge', 'Kettlebell Lunge', 'legs', 'legs'),
  ex('kettlebell-front-squat', 'Kettlebell Front Squat', 'legs', 'legs'),
  ex('kettlebell-slingshot', 'Kettlebell Slingshot', 'core', 'core'),
  ex('kettlebell-figure-8', 'Kettlebell Figure-8', 'full_body', 'full_body'),
  ex('kettlebell-high-pull', 'Kettlebell High Pull', 'full_body', 'full_body'),
  ex('double-kb-swing', 'Double Kettlebell Swing', 'full_body', 'full_body'),
  ex('double-kb-clean', 'Double Kettlebell Clean', 'full_body', 'full_body'),
  ex('double-kb-press', 'Double Kettlebell Press', 'shoulders', 'shoulders'),
  ex('kettlebell-renegade-row', 'Kettlebell Renegade Row', 'back', 'back'),
  ex('kettlebell-overhead-carry', 'Kettlebell Overhead Carry', 'full_body', 'full_body'),
];

// ── Bands / Resistance Bands ──
const BANDS: Exercise[] = [
  ex('band-pull-apart', 'Band Pull-Apart', 'shoulders', 'shoulders'),
  ex('band-bicep-curl', 'Band Bicep Curl', 'arms', 'arms'),
  ex('band-tricep-pushdown', 'Band Tricep Pushdown', 'arms', 'arms'),
  ex('band-shoulder-press', 'Band Shoulder Press', 'shoulders', 'shoulders'),
  ex('band-lateral-raise', 'Band Lateral Raise', 'shoulders', 'shoulders'),
  ex('band-row', 'Band Row', 'back', 'back'),
  ex('band-squat', 'Band Squat', 'legs', 'legs'),
  ex('band-deadlift', 'Band Deadlift', 'legs', 'legs'),
  ex('band-chest-press', 'Band Chest Press', 'chest', 'chest'),
  ex('band-chest-fly', 'Band Chest Fly', 'chest', 'chest'),
  ex('band-face-pull', 'Band Face Pull', 'shoulders', 'shoulders'),
  ex('band-good-morning', 'Band Good Morning', 'back', 'back'),
  ex('band-glute-bridge', 'Band Glute Bridge', 'legs', 'legs'),
  ex('band-hip-thrust', 'Band Hip Thrust', 'legs', 'legs'),
  ex('band-pallof-press', 'Band Pallof Press', 'core', 'core'),
  ex('band-lat-pulldown', 'Band Lat Pulldown', 'back', 'back'),
  ex('band-overhead-tricep-ext', 'Band Overhead Tricep Extension', 'arms', 'arms'),
  ex('band-reverse-fly', 'Band Reverse Fly', 'shoulders', 'shoulders'),
  ex('band-front-raise', 'Band Front Raise', 'shoulders', 'shoulders'),
  ex('band-leg-curl', 'Band Leg Curl', 'legs', 'legs'),
  ex('band-kickback-glute', 'Band Kickback (Glute)', 'legs', 'legs'),
  ex('band-monster-walk', 'Band Monster Walk', 'legs', 'legs'),
  ex('band-clamshell', 'Band Clamshell', 'legs', 'legs'),
  ex('band-woodchop', 'Band Woodchop', 'core', 'core'),
  ex('band-upright-row', 'Band Upright Row', 'shoulders', 'shoulders'),
  ex('band-hammer-curl', 'Band Hammer Curl', 'arms', 'arms'),
  ex('band-reverse-curl', 'Band Reverse Curl', 'arms', 'arms'),
  ex('band-push-up', 'Band Push-Up (Banded)', 'chest', 'chest'),
  ex('band-squat-to-press', 'Band Squat to Press', 'full_body', 'full_body'),
  ex('band-lunge', 'Band Lunge', 'legs', 'legs'),
  ex('band-lateral-walk', 'Band Lateral Walk', 'legs', 'legs'),
  ex('band-standing-crunch', 'Band Standing Crunch', 'core', 'core'),
  ex('band-seated-row', 'Band Seated Row', 'back', 'back'),
  ex('band-external-rotation', 'Band External Rotation', 'shoulders', 'shoulders'),
  ex('band-internal-rotation', 'Band Internal Rotation', 'shoulders', 'shoulders'),
  ex('band-leg-extension', 'Band Leg Extension', 'legs', 'legs'),
  ex('band-single-leg-deadlift', 'Band Single-Leg Deadlift', 'legs', 'legs'),
  ex('band-fly-incline', 'Band Fly (Incline)', 'chest', 'chest'),
  ex('band-kickback-tricep', 'Band Kickback (Tricep)', 'arms', 'arms'),
];

// ── Calisthenics / Bodyweight ──
const CALISTHENICS: Exercise[] = [
  ex('pull-up', 'Pull-Up', 'calisthenics', 'calisthenics'),
  ex('chin-up', 'Chin-Up', 'calisthenics', 'calisthenics'),
  ex('push-up', 'Push-Up', 'calisthenics', 'calisthenics'),
  ex('dip', 'Dip', 'calisthenics', 'calisthenics'),
  ex('bodyweight-squat', 'Bodyweight Squat', 'calisthenics', 'calisthenics'),
  ex('pistol-squat', 'Pistol Squat', 'calisthenics', 'calisthenics'),
  ex('muscle-up', 'Muscle-Up', 'calisthenics', 'calisthenics'),
  ex('inverted-row', 'Inverted Row', 'calisthenics', 'calisthenics'),
  ex('hanging-leg-raise', 'Hanging Leg Raise', 'calisthenics', 'calisthenics'),
  ex('burpee', 'Burpee', 'calisthenics', 'calisthenics'),
  ex('diamond-push-up', 'Diamond Push-Up', 'calisthenics', 'calisthenics'),
  ex('pike-push-up', 'Pike Push-Up', 'calisthenics', 'calisthenics'),
  ex('handstand-push-up', 'Handstand Push-Up', 'calisthenics', 'calisthenics'),
  ex('archer-push-up', 'Archer Push-Up', 'calisthenics', 'calisthenics'),
  ex('archer-pull-up', 'Archer Pull-Up', 'calisthenics', 'calisthenics'),
  ex('wide-grip-pull-up', 'Wide-Grip Pull-Up', 'calisthenics', 'calisthenics'),
  ex('close-grip-pull-up', 'Close-Grip Pull-Up', 'calisthenics', 'calisthenics'),
  ex('bodyweight-lunge', 'Lunge (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('jump-squat', 'Jump Squat', 'calisthenics', 'calisthenics'),
  ex('box-jump', 'Box Jump', 'calisthenics', 'calisthenics'),
  ex('bodyweight-step-up', 'Step-Up (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('glute-bridge', 'Glute Bridge', 'calisthenics', 'calisthenics'),
  ex('single-leg-glute-bridge', 'Single-Leg Glute Bridge', 'calisthenics', 'calisthenics'),
  ex('nordic-hamstring-curl', 'Nordic Hamstring Curl', 'calisthenics', 'calisthenics'),
  ex('wall-sit', 'Wall Sit', 'calisthenics', 'calisthenics'),
  ex('bodyweight-calf-raise', 'Calf Raise (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('bear-crawl', 'Bear Crawl', 'calisthenics', 'calisthenics'),
  ex('l-sit', 'L-Sit', 'calisthenics', 'calisthenics'),
  ex('tuck-planche', 'Tuck Planche', 'calisthenics', 'calisthenics'),
  ex('front-lever', 'Front Lever', 'calisthenics', 'calisthenics'),
  ex('back-lever', 'Back Lever', 'calisthenics', 'calisthenics'),
  ex('human-flag', 'Human Flag', 'calisthenics', 'calisthenics'),
  ex('commando-pull-up', 'Commando Pull-Up', 'calisthenics', 'calisthenics'),
  ex('typewriter-pull-up', 'Typewriter Pull-Up', 'calisthenics', 'calisthenics'),
  ex('explosive-pull-up', 'Explosive Pull-Up', 'calisthenics', 'calisthenics'),
  ex('clapping-push-up', 'Clapping Push-Up', 'calisthenics', 'calisthenics'),
  ex('wide-push-up', 'Wide Push-Up', 'calisthenics', 'calisthenics'),
  ex('decline-push-up', 'Decline Push-Up', 'calisthenics', 'calisthenics'),
  ex('incline-push-up', 'Incline Push-Up', 'calisthenics', 'calisthenics'),
  ex('hindu-push-up', 'Hindu Push-Up', 'calisthenics', 'calisthenics'),
  ex('spiderman-push-up', 'Spiderman Push-Up', 'calisthenics', 'calisthenics'),
  ex('pseudo-planche-push-up', 'Pseudo Planche Push-Up', 'calisthenics', 'calisthenics'),
  ex('ring-dip', 'Ring Dip', 'calisthenics', 'calisthenics'),
  ex('ring-push-up', 'Ring Push-Up', 'calisthenics', 'calisthenics'),
  ex('ring-row', 'Ring Row', 'calisthenics', 'calisthenics'),
  ex('ring-muscle-up', 'Ring Muscle-Up', 'calisthenics', 'calisthenics'),
  ex('strict-bar-muscle-up', 'Strict Bar Muscle-Up', 'calisthenics', 'calisthenics'),
  ex('skin-the-cat', 'Skin the Cat', 'calisthenics', 'calisthenics'),
  ex('reverse-lunge-bw', 'Reverse Lunge (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('walking-lunge-bw', 'Walking Lunge (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('lateral-lunge-bw', 'Lateral Lunge (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('bw-bulgarian-split-squat', 'Bulgarian Split Squat (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('cossack-squat', 'Cossack Squat', 'calisthenics', 'calisthenics'),
  ex('sissy-squat', 'Sissy Squat', 'calisthenics', 'calisthenics'),
  ex('shrimp-squat', 'Shrimp Squat', 'calisthenics', 'calisthenics'),
  ex('single-leg-calf-raise-bw', 'Single-Leg Calf Raise (Bodyweight)', 'calisthenics', 'calisthenics'),
  ex('donkey-calf-raise', 'Donkey Calf Raise', 'calisthenics', 'calisthenics'),
  ex('mountain-climber', 'Mountain Climber', 'calisthenics', 'calisthenics'),
  ex('high-knees', 'High Knees', 'calisthenics', 'calisthenics'),
  ex('jumping-lunge', 'Jumping Lunge', 'calisthenics', 'calisthenics'),
  ex('tuck-jump', 'Tuck Jump', 'calisthenics', 'calisthenics'),
  ex('depth-jump', 'Depth Jump', 'calisthenics', 'calisthenics'),
  ex('broad-jump', 'Broad Jump', 'calisthenics', 'calisthenics'),
];

// ── Core ──
const CORE: Exercise[] = [
  ex('plank', 'Plank', 'core', 'core'),
  ex('ab-wheel-rollout', 'Ab Wheel Rollout', 'core', 'core'),
  ex('cable-crunch', 'Cable Crunch', 'core', 'core'),
  ex('russian-twist', 'Russian Twist', 'core', 'core'),
  ex('pallof-press', 'Pallof Press', 'core', 'core'),
  ex('dead-bug', 'Dead Bug', 'core', 'core'),
  ex('side-plank', 'Side Plank', 'core', 'core'),
  ex('bicycle-crunch', 'Bicycle Crunch', 'core', 'core'),
  ex('v-up', 'V-Up', 'core', 'core'),
  ex('dragon-flag', 'Dragon Flag', 'core', 'core'),
  ex('hollow-body-hold', 'Hollow Body Hold', 'core', 'core'),
  ex('decline-sit-up', 'Decline Sit-Up', 'core', 'core'),
  ex('weighted-plank', 'Weighted Plank', 'core', 'core'),
  ex('hanging-knee-raise', 'Hanging Knee Raise', 'core', 'core'),
  ex('toes-to-bar', 'Toes-to-Bar', 'core', 'core'),
  ex('copenhagen-plank', 'Copenhagen Plank', 'core', 'core'),
  ex('suitcase-carry', 'Suitcase Carry', 'core', 'core'),
  ex('weighted-sit-up', 'Weighted Sit-Up', 'core', 'core'),
  ex('crunch', 'Crunch', 'core', 'core'),
  ex('sit-up', 'Sit-Up', 'core', 'core'),
  ex('flutter-kick', 'Flutter Kick', 'core', 'core'),
  ex('scissor-kick', 'Scissor Kick', 'core', 'core'),
  ex('lying-leg-raise', 'Leg Raise (Lying)', 'core', 'core'),
  ex('reverse-crunch', 'Reverse Crunch', 'core', 'core'),
  ex('woodchop-dumbbell', 'Woodchop (Dumbbell)', 'core', 'core'),
  ex('side-bend-dumbbell', 'Side Bend (Dumbbell)', 'core', 'core'),
  ex('plank-row', 'Plank Row', 'core', 'core'),
  ex('plank-shoulder-tap', 'Plank Shoulder Tap', 'core', 'core'),
  ex('body-saw', 'Body Saw', 'core', 'core'),
  ex('stir-the-pot', 'Stir the Pot', 'core', 'core'),
  ex('bird-dog', 'Bird Dog', 'core', 'core'),
  ex('superman-hold', 'Superman Hold', 'core', 'core'),
  ex('hanging-windshield-wiper', 'Hanging Windshield Wiper', 'core', 'core'),
  ex('ghd-sit-up', 'GHD Sit-Up', 'core', 'core'),
  ex('ghd-hip-extension', 'GHD Hip Extension', 'core', 'core'),
  ex('hyperextension', 'Hyperextension', 'core', 'core'),
  ex('weighted-hyperextension', 'Weighted Hyperextension', 'core', 'core'),
];

// ── Cardio ──
const CARDIO: Exercise[] = [
  ex('rowing-machine', 'Rowing Machine', 'cardio', 'cardio'),
  ex('assault-bike', 'Assault Bike', 'cardio', 'cardio'),
  ex('sled-push', 'Sled Push', 'cardio', 'cardio'),
  ex('battle-ropes', 'Battle Ropes', 'cardio', 'cardio'),
  ex('treadmill-run', 'Treadmill Run', 'cardio', 'cardio'),
  ex('treadmill-walk', 'Treadmill Walk', 'cardio', 'cardio'),
  ex('treadmill-incline-walk', 'Treadmill Incline Walk', 'cardio', 'cardio'),
  ex('stairmaster', 'Stairmaster', 'cardio', 'cardio'),
  ex('stationary-bike', 'Stationary Bike', 'cardio', 'cardio'),
  ex('spin-bike', 'Spin Bike', 'cardio', 'cardio'),
  ex('elliptical', 'Elliptical', 'cardio', 'cardio'),
  ex('jump-rope', 'Jump Rope', 'cardio', 'cardio'),
  ex('sled-pull', 'Sled Pull', 'cardio', 'cardio'),
  ex('ski-erg', 'Ski Erg', 'cardio', 'cardio'),
  ex('swimming', 'Swimming', 'cardio', 'cardio'),
  ex('sprints', 'Sprints', 'cardio', 'cardio'),
  ex('hill-sprints', 'Hill Sprints', 'cardio', 'cardio'),
  ex('prowler-push', 'Prowler Push', 'cardio', 'cardio'),
  ex('double-under', 'Double Under', 'cardio', 'cardio'),
  ex('air-bike', 'Air Bike', 'cardio', 'cardio'),
  ex('recumbent-bike', 'Recumbent Bike', 'cardio', 'cardio'),
  ex('outdoor-run', 'Outdoor Run', 'cardio', 'cardio'),
  ex('outdoor-walk', 'Outdoor Walk', 'cardio', 'cardio'),
  ex('hiking', 'Hiking', 'cardio', 'cardio'),
  ex('stair-climbing', 'Stair Climbing', 'cardio', 'cardio'),
  ex('rowing-sprint-500m', 'Rowing Sprint (500m)', 'cardio', 'cardio'),
  ex('bike-sprint', 'Bike Sprint', 'cardio', 'cardio'),
];

// ── Strongman / Specialty / Full Body ──
const STRONGMAN: Exercise[] = [
  ex('tire-flip', 'Tire Flip', 'full_body', 'full_body'),
  ex('farmers-walk', 'Farmers Walk', 'full_body', 'full_body'),
  ex('rope-climb', 'Rope Climb', 'full_body', 'full_body'),
  ex('sledgehammer-slam', 'Sledgehammer Slam', 'full_body', 'full_body'),
  ex('medicine-ball-slam', 'Medicine Ball Slam', 'full_body', 'full_body'),
  ex('wall-ball', 'Wall Ball', 'full_body', 'full_body'),
  ex('atlas-stone', 'Atlas Stone', 'full_body', 'full_body'),
  ex('log-press', 'Log Press', 'shoulders', 'shoulders'),
  ex('yoke-walk', 'Yoke Walk', 'full_body', 'full_body'),
  ex('axle-deadlift', 'Axle Deadlift', 'back', 'back'),
  ex('axle-press', 'Axle Press', 'shoulders', 'shoulders'),
  ex('sandbag-carry', 'Sandbag Carry', 'full_body', 'full_body'),
  ex('sandbag-clean', 'Sandbag Clean', 'full_body', 'full_body'),
  ex('sandbag-squat', 'Sandbag Squat', 'legs', 'legs'),
  ex('sandbag-shouldering', 'Sandbag Shouldering', 'full_body', 'full_body'),
  ex('stone-over-bar', 'Stone Over Bar', 'full_body', 'full_body'),
  ex('keg-carry', 'Keg Carry', 'full_body', 'full_body'),
  ex('frame-carry', 'Frame Carry', 'full_body', 'full_body'),
  ex('circus-dumbbell-press', 'Circus Dumbbell Press', 'shoulders', 'shoulders'),
  ex('car-deadlift-sim', 'Car Deadlift (Simulator)', 'back', 'back'),
  ex('hand-over-hand-sled-pull', 'Hand-Over-Hand Sled Pull', 'back', 'back'),
];

// ── Grip / Forearm Specialty ──
const GRIP: Exercise[] = [
  ex('dead-hang', 'Dead Hang', 'back', 'back'),
  ex('towel-pull-up', 'Towel Pull-Up', 'back', 'back'),
  ex('fat-grip-curl', 'Fat Grip Curl', 'arms', 'arms'),
  ex('plate-pinch-hold', 'Plate Pinch Hold', 'arms', 'arms'),
  ex('gripper-close', 'Gripper Close', 'arms', 'arms'),
  ex('wrist-roller', 'Wrist Roller', 'arms', 'arms'),
];

// ── Stretching / Mobility ──
const MOBILITY: Exercise[] = [
  ex('foam-rolling', 'Foam Rolling (Full Body)', 'core', 'core'),
  ex('hip-90-90', 'Hip 90/90 Stretch', 'legs', 'legs'),
  ex('worlds-greatest-stretch', "World's Greatest Stretch", 'full_body', 'full_body'),
  ex('cat-cow', 'Cat-Cow', 'core', 'core'),
  ex('downward-dog', 'Downward Dog', 'full_body', 'full_body'),
  ex('couch-stretch', 'Couch Stretch', 'legs', 'legs'),
  ex('pigeon-stretch', 'Pigeon Stretch', 'legs', 'legs'),
  ex('shoulder-dislocate', 'Shoulder Dislocate', 'shoulders', 'shoulders'),
  ex('thoracic-spine-rotation', 'Thoracic Spine Rotation', 'back', 'back'),
  ex('banded-shoulder-stretch', 'Banded Shoulder Stretch', 'shoulders', 'shoulders'),
  ex('pec-stretch-doorway', 'Pec Stretch (Doorway)', 'chest', 'chest'),
  ex('hamstring-stretch', 'Hamstring Stretch (Standing)', 'legs', 'legs'),
  ex('quad-stretch', 'Quad Stretch (Standing)', 'legs', 'legs'),
  ex('calf-stretch', 'Calf Stretch', 'legs', 'legs'),
  ex('it-band-foam-roll', 'IT Band Foam Roll', 'legs', 'legs'),
  ex('lacrosse-ball-pec', 'Lacrosse Ball Pec Release', 'chest', 'chest'),
  ex('banded-hip-flexor-stretch', 'Banded Hip Flexor Stretch', 'legs', 'legs'),
  ex('jefferson-curl', 'Jefferson Curl', 'back', 'back'),
  ex('scorpion-stretch', 'Scorpion Stretch', 'core', 'core'),
  ex('wall-slide', 'Wall Slide', 'shoulders', 'shoulders'),
];

/**
 * Full local exercise catalog. Baseline lifts are first.
 */
export const EXERCISE_CATALOG_FALLBACK: Exercise[] = [
  ...BASELINE,
  ...BARBELL,
  ...OLYMPIC,
  ...EZ_CURL,
  ...TRAP_BAR,
  ...SMITH,
  ...DUMBBELL,
  ...CABLE,
  ...KETTLEBELL,
  ...BANDS,
  ...CALISTHENICS,
  ...CORE,
  ...CARDIO,
  ...STRONGMAN,
  ...GRIP,
  ...MOBILITY,
];

/**
 * IDs of the 5 baseline lifts for prominence sorting.
 */
export const BASELINE_IDS = new Set(BASELINE.map((e) => e.id));

/**
 * Known body part strings (lowercase).
 */
export const KNOWN_BODY_PARTS = new Set([
  'chest', 'back', 'legs', 'shoulders', 'arms', 'core', 'full_body', 'cardio', 'biceps', 'triceps', 'calisthenics',
]);

/**
 * Given a duel exercise_name, return the list of canonical exercise names to query.
 * If the name is a body part (e.g. "Shoulders"), returns all exercises in that category.
 * If it's a specific exercise, returns just that name in an array.
 * Returns null if the input is empty.
 */
/** Map exercise names to their common lift_key equivalents */
const NAME_TO_LIFT_KEY: Record<string, string> = {
  'back squat': 'squat',
  'bench press': 'bench',
  'deadlift': 'deadlift',
  'weighted pull-up': 'pull-up',
  'dead hang': 'dead-hang',
  "farmer's carry static hold": 'farmers-carry',
  'overhead press': 'ohp',
};

export function resolveDuelExerciseNames(exerciseName: string | null | undefined): string[] | null {
  if (!exerciseName) return null;
  const lower = exerciseName.toLowerCase();
  if (KNOWN_BODY_PARTS.has(lower)) {
    const names = EXERCISE_CATALOG_FALLBACK
      .filter(e => (e.body_part ?? '').toLowerCase() === lower || (e.category ?? '').toLowerCase() === lower)
      .map(e => e.name);
    // Also include lift_key variants for catalog exercises
    const keys = names.map(n => NAME_TO_LIFT_KEY[n.toLowerCase()]).filter(Boolean) as string[];
    return [...new Set([...names, ...keys])];
  }
  // Single exercise — include its lift_key variant too
  const liftKey = NAME_TO_LIFT_KEY[lower];
  return liftKey ? [exerciseName, liftKey] : [exerciseName];
}
